// components/ParamsCard/ForecastSelector.tsx
import React, { useEffect, useCallback } from 'react';
import { Select } from 'antd';
import DashboardContext from '@/contexts/DashboardContext';
import { Toggle, Tooltip, Combobox, ComboboxInput, ComboboxContent, ComboboxEmpty, ComboboxList, ComboboxItem } from '@hydrodigital/design-system';

const { Option } = Select;

interface ForecastSelectorProps {
	showButton: boolean;
	isDateRangeMoreThan24Hours: boolean;
	showPastForecast: boolean;
	setShowPastForecast: (checked: boolean) => void;
	selectedSimulation: string | undefined;
	setSelectedSimulation: (id: string | undefined) => void;
	onFetchSimulations: () => void;
	cleanAreaSeries: () => void;
	simulationList: any[];
	isLoadingSimulations?: boolean;
	hasSimulationError?: boolean;
}

const ForecastSelector = ({
	showButton,
	isDateRangeMoreThan24Hours,
	showPastForecast,
	setShowPastForecast,
	selectedSimulation,
	setSelectedSimulation,
	onFetchSimulations,
	cleanAreaSeries,
	simulationList = [],
	isLoadingSimulations = false,
	hasSimulationError = false,
}: ForecastSelectorProps) => {
	const { value: context } = React.useContext(DashboardContext);

  // Memoize the switch change handler to prevent infinite re-renders
  const handleSwitchChange = useCallback((checked: boolean) => {
    setSelectedSimulation(undefined);
    setShowPastForecast(checked);
  }, [setSelectedSimulation, setShowPastForecast]);

  useEffect(() => {
    cleanAreaSeries();
    setSelectedSimulation(undefined);
    
    if (isDateRangeMoreThan24Hours) {
      setShowPastForecast(false);
    }
  }, [context.selectedDate, isDateRangeMoreThan24Hours]);

  useEffect(() => {
		cleanAreaSeries();
		setSelectedSimulation(undefined);

		if (isDateRangeMoreThan24Hours) {
			setShowPastForecast(false);
		} else {
			if (showPastForecast) {
				onFetchSimulations();
			}
		}
	}, [context.selectedDate, showPastForecast, isDateRangeMoreThan24Hours]);

	return (
		<div className="flex items-center gap-2">
			
			<Tooltip
				
				slot={
					isDateRangeMoreThan24Hours || !showButton ? (
						<div>
							<div style={{ fontSize: '14px', fontWeight: 600 }}>Enable Historical vs Forecast</div>
							<div style={{ fontSize: '12px', marginTop: 4, whiteSpace: 'normal' }}>
								To enable, select only one parameter with a forecast source and a date range of 24
								hours or less.
							</div>
						</div>
					) : 'Historical vs Forecast'
				}
				position='down-right'
			>
				<span>
					<Toggle
						checked={showPastForecast}
						disabled={isDateRangeMoreThan24Hours || !showButton}
						checkedText="Past Forecast"
						uncheckedText="Past Forecast"
						size='small'
						onChange={({target: {checked}}) => {
							setSelectedSimulation(undefined);
							setShowPastForecast(checked);
						}}
						style={{ width: '110px' }}
					/>
				</span>
			</Tooltip>

			{showPastForecast && (
				<Combobox 
					items={simulationList
							? 
							simulationList
						.sort((a, b) => {
							return new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime();
						})
						.map((opt) => ({
							label: `${opt.startedAt} ${opt.strategyName ?? ''}`,
							value: opt.id
						}))
							: []} 
					value={selectedSimulation} 
					onValueChange={(val): any => {
						setSelectedSimulation(val);
					}}
					filterOption={(option, searchValue) =>
						option?.label?.toLowerCase().includes(searchValue.toLowerCase())
					}
					size='small'
					searchable={true}
					disabled={isLoadingSimulations || hasSimulationError}
					state={hasSimulationError ? 'error' : undefined}
				>
					<ComboboxInput placeholder={isLoadingSimulations 
							? "Loading forecast data..." 
							: hasSimulationError 
							? "Failed to load forecasts" 
							: "Forecast from"} />
					<ComboboxContent>
						<ComboboxEmpty>No items found.</ComboboxEmpty>
						<ComboboxList>
						{(item) => (
							<ComboboxItem key={item.value} value={item.value}>
							{item.label}
							</ComboboxItem>
						)}
						</ComboboxList>
					</ComboboxContent>
				</Combobox>
			)}
		</div>
	);
};

export default ForecastSelector;
